// Test-print builders for the native (Capacitor Android) printing settings
// modal, ported from the legacy NativePrintSettingsModal: a generic ESC/POS
// test, a jszpl label test honoring the user's sticker settings, and the
// multi-codepage sample used to pick the right encoding visually.
import { getCurrentInstance, ref } from 'vue';
import {
  Alignment,
  AlignmentValue,
  FontFamily,
  FontFamilyName,
  Label,
  PrintDensity,
  PrintDensityName,
  Spacing,
  Text,
} from 'jszpl';
import { usePrinting, usePrintingStore } from '@/composition/use-printing';
import { useUi } from '@/composition/use-ui';
import { AlertType } from '@/interfaces/IAlert';
import { IPrinter } from '@/interfaces/IPrinter';
import { IPrintingSettings } from '@/interfaces/IPrintingSettings';
import {
  CODEPAGE_CYCLE,
  buildCodepageMegaTest,
  buildCodepageSample,
  buildLongQrTestDoc,
  buildTestTicket,
  buildZplCalibrationDoc,
} from '@/lib/print/test-ticket';
import { CODEPAGE_ITEMS, PRINTER_CODEPAGES } from '@/lib/print/codepages';
import {
  IEscposPrinterSettings,
  IZplPrinterSettings,
  injectZplOffsets,
  zplSettingsToSticker,
} from '@/lib/print/printer-settings';

/** Codepages sampled by testEncodings (canonical shared list, print order). */
const TEST_CODEPAGES = PRINTER_CODEPAGES;

export const NATIVE_CODEPAGE_ITEMS = CODEPAGE_ITEMS;

/**
 * Runs before the guarded print (e.g. a network reachability probe). Return
 * false to abort the test — the preflight owns its own user feedback.
 */
export type ITestPreflight = () => Promise<boolean>;

/**
 * MODULE scope on purpose: the modal's tabs are v-if-mounted, so a running
 * test (testEncodings sends 30+ sequential jobs) must keep every instance's
 * buttons disabled — a per-instance ref would reset on tab switch and let a
 * second test interleave its bytes into the running one's job sequence.
 */
const testingPrinterId = ref<IPrinter['id'] | null>(null);

export function useNativePrintTests() {
  const printing = usePrinting();
  const store = usePrintingStore();
  const ui = useUi();

  // Null-safe: also callable outside a component context (shared
  // composables, unit tests) — falls back to the raw key there.
  const proxy = getCurrentInstance()?.proxy ?? null;
  function i18nT(key: string): string {
    return proxy ? String(proxy.$t(key)) : key;
  }

  /** Resolves true only when the document was actually handed to the
   * transport (guard no-op, failed preflight and print errors → false) —
   * the wizard's Test step needs the outcome to drive its confirmation UI. */
  async function runTest(
    printer: IPrinter,
    print: () => Promise<void>,
    preflight?: ITestPreflight,
    // "Sent" honesty: a successful TCP/USB write is NOT proof of paper out
    // (a ZPL-only printer silently swallows ESC/POS bytes), so the default
    // success toast tells the user to check the physical output.
    successToastKey = 'printingV2.nativeTestSentCheckPaper'
  ): Promise<boolean> {
    if (testingPrinterId.value !== null) return false;
    testingPrinterId.value = printer.id;
    try {
      if (preflight && !(await preflight())) return false;
      await print();
      ui.showToast(AlertType.Success, i18nT(successToastKey));
      return true;
    } catch (e) {
      console.error('[NativePrintTests] test failed', e);
      const detail = e instanceof Error ? `: ${e.message}` : '';
      ui.showToast(
        AlertType.Error,
        `${i18nT('printingV2.testFailed')}${detail}`
      );
      return false;
    } finally {
      testingPrinterId.value = null;
    }
  }

  /**
   * Sends the shared ESC/POS test ticket (charset sample + width-calibration
   * block), built with the printer's RESOLVED per-printer settings — the
   * destination encoding is only the fallback for printers without explicit
   * settings (no guard — internal).
   */
  async function printEscPosTestDoc(
    printer: IPrinter,
    settings: IPrintingSettings
  ): Promise<void> {
    const { escpos } = store.printerSettingsOf(printer, settings.encoding);
    await printing.printByteArray(printer, buildTestTicket(escpos));
  }

  /** Sends the ZPL test label (no guard — internal). */
  async function printStickerTestDoc(printer: IPrinter): Promise<void> {
    // Resolved per-printer ZPL settings (explicit → legacy localStorage →
    // defaults), rendered through the same jszpl pipeline real labels use.
    const { zpl, zplExplicit } = store.printerSettingsOf(printer);
    const settings = zplSettingsToSticker(zpl);

    const label = new Label();
    label.printDensity = new PrintDensity(
      PrintDensityName[settings.printDensity]
    );
    label.width = settings.width;
    label.height = settings.height;
    label.padding = new Spacing(
      settings.padding.left,
      settings.padding.top,
      settings.padding.right,
      settings.padding.bottom
    );

    const text = new Text();
    label.content.push(text);
    let fontFamily = FontFamilyName.F;
    switch (settings.fontSize) {
      case 'xs':
        fontFamily = FontFamilyName.B;
        break;
      case 's':
        fontFamily = FontFamilyName.D;
        break;
      case 'm':
        fontFamily = FontFamilyName.F;
        break;
      case 'l':
        fontFamily = FontFamilyName.E;
        break;
    }

    text.fontFamily = new FontFamily(fontFamily);
    text.text =
      'Prueba de Impresión\nEsta es una prueba de impresión del sistema de etiquetas de QueResto\nhttps://queresto.com';
    text.verticalAlignment = new Alignment(AlignmentValue.Start);
    text.horizontalAlignment = new Alignment(AlignmentValue.Start);

    // Offset triplet only with EXPLICIT settings — the test must mirror how
    // real labels print (^LH/^LS/^LT persist across jobs on real Zebra
    // firmware; a test must never leave state that real labels won't reset).
    const doc = zplExplicit
      ? injectZplOffsets(label.generateZPL(), zpl)
      : label.generateZPL();
    await printing.printZpl(printer, doc);
  }

  /** Generic ESC/POS test (accents + QR) using the destination's codepage. */
  async function testEscPos(
    printer: IPrinter,
    settings: IPrintingSettings,
    preflight?: ITestPreflight
  ): Promise<boolean> {
    return runTest(
      printer,
      () => printEscPosTestDoc(printer, settings),
      preflight
    );
  }

  /** ZPL label test honoring the user's persisted sticker settings. */
  async function testSticker(
    printer: IPrinter,
    preflight?: ITestPreflight
  ): Promise<boolean> {
    return runTest(printer, () => printStickerTestDoc(printer), preflight);
  }

  /**
   * For a printer assigned as sticker AND ticket/comanda: both documents in
   * ONE runTest invocation. WARNING: never call testEscPos + testSticker
   * back-to-back — each holds the module-scope testingPrinterId guard, so
   * the second (nested or immediate) call silently no-ops.
   */
  async function testBoth(
    printer: IPrinter,
    settings: IPrintingSettings,
    preflight?: ITestPreflight
  ): Promise<boolean> {
    return runTest(
      printer,
      async () => {
        await printEscPosTestDoc(printer, settings);
        await printStickerTestDoc(printer);
      },
      preflight
    );
  }

  /**
   * Prints the 3 numbered CODEPAGE_CYCLE samples (desktop-parity trouble
   * flow): the user picks the sample whose Spanish characters printed
   * readable. The 31-codepage testEncodings stays as the Preferences
   * escape hatch for exotic printers.
   */
  async function printCodepageSamples(printer: IPrinter): Promise<boolean> {
    return runTest(
      printer,
      async () => {
        // The printer's cut mode, not the 'modern' default: on a
        // legacy-cut printer the samples must still separate.
        const { escpos } = store.printerSettingsOf(printer);
        for (let i = 0; i < CODEPAGE_CYCLE.length; i++) {
          const bytes = buildCodepageSample(
            i + 1,
            CODEPAGE_CYCLE[i],
            escpos.cutMode
          );
          await printing.printByteArray(printer, bytes, {
            kind: 'test',
            label: `${i18nT('printingV2.kindTest')} ${i + 1}`,
          });
        }
      },
      undefined,
      'printingV2.helpSamplesSent'
    );
  }

  /**
   * Prints one accented line per known codepage so the user can visually
   * pick the encoding their printer renders correctly. `overrides` layer on
   * top of the resolved settings — the edit modal tests with its in-flight
   * (possibly unsaved) form values.
   */
  async function testEncodings(
    printer: IPrinter,
    overrides?: Partial<IEscposPrinterSettings>
  ): Promise<boolean> {
    return runTest(printer, async () => {
      // One buffer → one print job: per-line jobs could interleave with a
      // concurrent auto-print on the same printer, and each line would also
      // retry independently on failure. Built with the printer's resolved
      // settings (width/model/cut) — the sampled codepages are fixed.
      const { escpos } = store.printerSettingsOf(printer);
      const bytes = buildCodepageMegaTest(TEST_CODEPAGES, {
        ...escpos,
        ...overrides,
      });
      await printing.printByteArray(printer, bytes);
    });
  }

  /**
   * AFIP-length QR test rendered with the printer's qrMode (`overrides`
   * layer the edit modal's in-flight form values on top).
   */
  async function testQr(
    printer: IPrinter,
    overrides?: Partial<IEscposPrinterSettings>
  ): Promise<boolean> {
    return runTest(printer, async () => {
      const { escpos } = store.printerSettingsOf(printer);
      const bytes = await buildLongQrTestDoc({ ...escpos, ...overrides });
      await printing.printByteArray(printer, bytes);
    });
  }

  /** ZPL geometry-calibration print with the given (form) settings. */
  async function testZplCalibration(
    printer: IPrinter,
    zpl: IZplPrinterSettings
  ): Promise<boolean> {
    return runTest(printer, () =>
      printing.printZpl(printer, buildZplCalibrationDoc(zpl))
    );
  }

  return {
    testingPrinterId,
    testEscPos,
    testSticker,
    testBoth,
    testEncodings,
    testQr,
    testZplCalibration,
    printCodepageSamples,
  };
}
